import React, { useState } from 'react';
import { Box, Card, Typography, IconButton, Menu, MenuItem } from '@mui/material';
import {
    MoreVert as MoreVertIcon,
    Edit as EditIcon,
    Visibility as VisibilityIcon,
    Delete as DeleteIcon,
    NotificationsActive as AlertIcon,
    Sensors as SensorsIcon,
    AddCircle as AddCircleIcon,
    CalendarMonth as CalendarIcon,
    SentimentVerySatisfied as HappyIcon,
    SentimentVeryDissatisfied as SadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Hive } from '@types';
import SimpleHiveIcon from './SimpleHiveIcon';

interface HiveCardProps {
    hive: Hive;
    viewSize?: 'xs' | 'm' | 'xl';
    alerts?: any[];
    onDelete?: (hive: Hive) => void;
}

const HiveCard: React.FC<HiveCardProps> = ({
    hive,
    viewSize = 'm',
    alerts = [],
    onDelete
}) => {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const xsView = viewSize === 'xs';
    const mView = viewSize === 'm';
    const xlView = viewSize === 'xl';

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleInspect = () => {
        navigate(`/inspections/create?hiveId=${hive.id}`);
        handleMenuClose();
    };

    const handleViewInspections = () => {
        navigate(`/hives/${hive.id}/inspections`);
        handleMenuClose();
    };

    const handleViewDetails = () => {
        navigate(`/hives/${hive.id}`);
        handleMenuClose();
    };

    const handleEdit = () => {
        navigate(`/hives/${hive.id}/edit`);
        handleMenuClose();
    };

    const handleViewMeasurements = () => {
        navigate(`/measurements/${hive.id}`);
        handleMenuClose();
    };

    const handleViewAlerts = () => {
        navigate(`/alerts?search=${hive.name}`);
        handleMenuClose();
    };

    const handleDeleteClick = () => {
        if (onDelete) {
            onDelete(hive);
        }
        handleMenuClose();
    };

    const hiveHasInspection = hive.last_inspection_date !== null && hive.last_inspection_date !== undefined;
    const hiveHasSensor = hive.sensors && hive.sensors.length > 0;
    const hiveEditable = hive.editable || hive.owner;
    const lastInspectedAt = hive.last_inspection_date_locale_date || hive.last_inspection_date;
    const isOverdue = (() => {
        if (!lastInspectedAt) return true;
        const inspected = new Date(lastInspectedAt);
        if (Number.isNaN(inspected.getTime())) return true;
        return (Date.now() - inspected.getTime()) / 86400000 > 14;
    })();
    const lastWasPoor = hive.impression != null && hive.impression <= 2;
    const lastWasGood = hive.impression != null && hive.impression >= 4;

    return (
        <Card
            sx={{
                position: 'relative',
                width: xsView ? '70px' : mView ? '120px' : '160px',
                height: 'auto',
                display: 'flex',
                flexDirection: 'column',
                border: `2px solid ${hive.color || '#F8B133'}`,
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                p: 0.5,
                '&:hover': {
                    boxShadow: 2,
                },
            }}
            onClick={() => handleViewDetails()}
        >
            {/* Header with name and menu */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 0.5,
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        fontSize: xsView ? '0.7rem' : mView ? '0.75rem' : '0.875rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        lineHeight: 1.2,
                    }}
                >
                    {hive.name}
                </Typography>

                {hiveEditable && (
                    <IconButton
                        size="small"
                        onClick={handleMenuClick}
                        sx={{
                            p: 0,
                            ml: 0.5,
                            mt: -0.5,
                        }}
                    >
                        <MoreVertIcon sx={{ fontSize: xsView ? 16 : 18 }} />
                    </IconButton>
                )}
            </Box>

            {/* Hive Icon */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    py: mView ? 0.5 : 0.3,
                    px: mView ? 0.5 : 0,
                }}
            >
                <SimpleHiveIcon size={viewSize} color={hive.color} />

                {/* Add Hive Button (bottom right of icon) - Hidden in XS view */}
                {hiveEditable && !xsView && (
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleInspect();
                        }}
                        aria-label="New inspection"
                        sx={{
                            position: 'absolute',
                            bottom: -4,
                            right: -4,
                            backgroundColor: '#666',
                            color: 'white',
                            width: { xs: 36, md: 28 },
                            height: { xs: 36, md: 28 },
                            '&:hover': {
                                backgroundColor: '#555',
                            },
                            p: 0,
                        }}
                    >
                        <AddCircleIcon sx={{ fontSize: { xs: 20, md: 16 } }} />
                    </IconButton>
                )}
            </Box>

            {!xsView && (hive.attention || isOverdue || lastWasPoor || lastWasGood) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25, mt: 0.5, minHeight: 18 }}>
                    {hive.attention && <AlertIcon sx={{ fontSize: 16, color: 'error.main' }} />}
                    {isOverdue && <CalendarIcon sx={{ fontSize: 16, color: 'warning.dark' }} />}
                    {lastWasPoor && <SadIcon sx={{ fontSize: 16, color: 'error.main' }} />}
                    {lastWasGood && !lastWasPoor && <HappyIcon sx={{ fontSize: 16, color: 'success.main' }} />}
                </Box>
            )}

            {/* Context Menu */}
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                onClick={(e) => e.stopPropagation()}
                PaperProps={{
                    sx: {
                        minWidth: 180,
                    },
                }}
            >
                <MenuItem onClick={handleViewDetails}>
                    <VisibilityIcon fontSize="small" sx={{ mr: 1.5 }} />
                    View Details
                </MenuItem>

                {hiveEditable && (
                    <MenuItem onClick={handleInspect}>
                        <EditIcon fontSize="small" sx={{ mr: 1.5 }} />
                        {hiveHasInspection ? 'New Inspection' : 'First Inspection'}
                    </MenuItem>
                )}

                {hiveHasInspection && (
                    <MenuItem onClick={handleViewInspections}>
                        <VisibilityIcon fontSize="small" sx={{ mr: 1.5 }} />
                        View Inspections
                    </MenuItem>
                )}

                <MenuItem onClick={handleViewAlerts}>
                    <AlertIcon fontSize="small" sx={{ mr: 1.5 }} />
                    View Alerts
                </MenuItem>

                {hiveHasSensor && (
                    <MenuItem onClick={handleViewMeasurements}>
                        <SensorsIcon fontSize="small" sx={{ mr: 1.5 }} />
                        View Measurements
                    </MenuItem>
                )}

                {hiveEditable && (
                    <>
                        <MenuItem onClick={handleEdit}>
                            <EditIcon fontSize="small" sx={{ mr: 1.5 }} />
                            Edit Hive
                        </MenuItem>
                        {hive.owner && (
                            <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                                <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} />
                                Delete Hive
                            </MenuItem>
                        )}
                    </>
                )}
            </Menu>
        </Card>
    );
};

export default HiveCard;
